import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  CardMedia,
  Chip,
  Container,
  Drawer,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Search as SearchIcon } from "../../icons";
import { ReceiptLong as ReceiptLongIcon } from "../../icons";
import { Storefront as StorefrontIcon } from "../../icons";
import { DeliveryDining as DeliveryDiningIcon } from "../../icons";
import { Schedule as ScheduleIcon } from "../../icons";
import { ChevronRight as ChevronRightIcon } from "../../icons";
import { ChevronLeft as ChevronLeftIcon } from "../../icons";
import { Close as CloseIcon } from "../../icons";
import { ExpandMore as ExpandMoreIcon } from "../../icons";
import { FilterList as FilterListIcon } from "../../icons";
import { LocationOn as LocationOnIcon } from "../../icons";
import { auth } from "../../firebase";
import { useGeolocation } from "../../hooks/useGeolocation";
import { getCityFromCoordinates } from "../../utils/geo";
import { T } from "../Translate";
import BottomToolbar from "../BottomToolbar";
import CustomToolbar from "../CustomToolbar";
import SheetDragHandle from "../../components/SheetDragHandle";
import logoSvg from "../../imgs/logo.png";
import logo1 from "../../imgs/logo1.png";
import { brandColors, barColors, themeMode } from "../../theme";
import { pageContentSx, DESKTOP_MEDIA_QUERY } from "../../pageLayout";
import { useDesktopSidebar } from "../../desktopSidebarContext";
import {
  MEAL_TYPES,
  STORE_KINDS,
  categoriesFor,
  storeKind,
  ORDER_STATUS,
  categoryOf,
  currentMealType,
  fetchOwnRestaurant,
  formatMoney,
  isRestaurantOpen,
  subscribeOrdersAsBuyer,
  subscribeRestaurants,
} from "./foodUtils";

const logo5 = `${process.env.PUBLIC_URL}/logo5.png`;
const ACTIVE_STATUSES = ["pending", "accepted", "on_the_way", "ready"];

const SORT_OPTIONS = [
  { id: "", label: "Recomendados" },
  { id: "near", label: "Más cerca" },
  { id: "fast", label: "Más rápido" },
  { id: "fee", label: "Menor envío" },
  { id: "name", label: "A-Z" },
];

// "30-45 min" -> 30, "1 h" -> 60. Sin dato va al final.
const deliveryMinutes = (restaurant) => {
  const text = String(restaurant.deliveryTime || "").toLowerCase();
  const n = Number(text.match(/\d+/)?.[0]);
  if (!n) return Infinity;
  return /h/.test(text) && !/min/.test(text) ? n * 60 : n;
};

const deliveryFeeOf = (restaurant) =>
  restaurant.delivery === false ? Infinity : Number(restaurant.deliveryFee) || 0;

const distanceKm = (from, restaurant) => {
  if (!from || typeof restaurant.latitude !== "number" || typeof restaurant.longitude !== "number") return Infinity;
  const rad = (deg) => (deg * Math.PI) / 180;
  const dLat = rad(restaurant.latitude - from.lat);
  const dLng = rad(restaurant.longitude - from.lng);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(rad(from.lat)) * Math.cos(rad(restaurant.latitude)) * Math.sin(dLng / 2) ** 2;
  return 12742 * Math.asin(Math.sqrt(a));
};

// Fila que se desliza de costado, con flechitas a los lados que aparecen
// solo cuando hay más contenido para ese lado.
const ScrollRow = ({ children, gap = 1, sx }) => {
  const ref = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const update = () => {
    const node = ref.current;
    if (!node) return;
    setEdges({
      left: node.scrollLeft > 4,
      right: node.scrollLeft + node.clientWidth < node.scrollWidth - 4,
    });
  };

  useLayoutEffect(() => {
    update();
    const node = ref.current;
    if (!node) return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const scrollBy = (direction) => {
    const node = ref.current;
    if (node) node.scrollBy({ left: direction * node.clientWidth * 0.7, behavior: "smooth" });
  };

  const arrowSx = (side) => ({
    position: "absolute",
    top: "50%",
    [side]: -4,
    transform: "translateY(-50%)",
    zIndex: 1,
    width: 32,
    height: 32,
    bgcolor: "background.paper",
    boxShadow: 2,
    "&:hover": { bgcolor: "background.paper" },
  });

  return (
    <Box sx={{ position: "relative", ...sx }}>
      {edges.left && (
        <IconButton size="small" aria-label="Ver anteriores" onClick={() => scrollBy(-1)} sx={arrowSx("left")}>
          <ChevronLeftIcon />
        </IconButton>
      )}
      <Box
        ref={ref}
        onScroll={update}
        sx={{ display: "flex", gap, overflowX: "auto", pb: 0.5, scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}
      >
        {children}
      </Box>
      {edges.right && (
        <IconButton size="small" aria-label="Ver más" onClick={() => scrollBy(1)} sx={arrowSx("right")}>
          <ChevronRightIcon />
        </IconButton>
      )}
    </Box>
  );
};

const normalize = (value) =>
  String(value || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

export const RestaurantCard = ({ restaurant }) => {
  const open = isRestaurantOpen(restaurant);
  const category = categoryOf(restaurant.category);
  return (
    <Paper
      component={Link}
      to={`/pedidos/${restaurant.id}`}
      variant="outlined"
      sx={{
        display: "block",
        textDecoration: "none",
        color: "inherit",
        borderRadius: 3,
        overflow: "hidden",
        transition: "box-shadow 0.15s ease",
        "&:hover": { boxShadow: 3 },
      }}
    >
      <Box sx={{ position: "relative", height: 140, bgcolor: "action.hover" }}>
        {restaurant.coverPhoto ? (
          <Box
            component="img"
            src={restaurant.coverPhoto}
            alt=""
            loading="lazy"
            sx={{ width: "100%", height: "100%", objectFit: "cover", filter: open ? "none" : "grayscale(0.8)" }}
          />
        ) : (
          <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 56 }}>
            {category.emoji}
          </Box>
        )}
        {!open && (
          <Chip
            label="Cerrado"
            size="small"
            sx={{ position: "absolute", top: 8, left: 8, bgcolor: "rgba(0,0,0,0.7)", color: "#fff", fontWeight: 600 }}
          />
        )}
        <Avatar
          src={restaurant.logo}
          sx={{ position: "absolute", left: 12, bottom: -22, width: 52, height: 52, border: "3px solid", borderColor: "background.paper", bgcolor: "primary.main" }}
        >
          {category.emoji}
        </Avatar>
      </Box>
      <Box sx={{ px: 1.5, pt: 3.5, pb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
          {restaurant.name}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {category.label}
          {restaurant.city ? ` · ${restaurant.city}` : ""}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 0.75, color: "text.secondary" }}>
          {restaurant.deliveryTime && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <ScheduleIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption">{restaurant.deliveryTime}</Typography>
            </Box>
          )}
          {restaurant.delivery !== false && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <DeliveryDiningIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption">
                {Number(restaurant.deliveryFee) > 0 ? `Envío ${formatMoney(restaurant.deliveryFee, restaurant.currency)}` : "Envío gratis"}
              </Typography>
            </Box>
          )}
          {restaurant.delivery === false && <Typography variant="caption">Solo retiro</Typography>}
        </Box>
      </Box>
    </Paper>
  );
};

const FoodHome = () => {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);
  const sidebarActive = useDesktopSidebar();
  const showTopBar = !sidebarActive || !isDesktop;
  const uid = auth.currentUser?.uid;

  const [restaurants, setRestaurants] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ownRestaurant, setOwnRestaurant] = useState(undefined);
  const [search, setSearch] = useState("");
  const [meal, setMeal] = useState(currentMealType);
  const [category, setCategory] = useState("");
  const [sort, setSort] = useState("");
  const [openOnly, setOpenOnly] = useState(false);
  const [freeDelivery, setFreeDelivery] = useState(false);
  const [withDelivery, setWithDelivery] = useState(false);
  const [noMinimum, setNoMinimum] = useState(false);
  const [sortAnchor, setSortAnchor] = useState(null);
  // En mobile la búsqueda y los filtros van en la barra de arriba, como en
  // Autos (cars.js): la lupa abre el buscador y el ícono de filtros una hoja.
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // ?tipo=super abre directo en supermercados (para compartir el link).
  const [searchParams, setSearchParams] = useSearchParams();
  const kind = searchParams.get("tipo") === "super" ? "market" : "food";
  const setKind = (next) => {
    setCategory("");
    setSearchParams(next === "market" ? { tipo: "super" } : {}, { replace: true });
  };
  const categories = categoriesFor(kind);

  useEffect(() => subscribeRestaurants(setRestaurants, (error) => {
    console.error("Error cargando locales:", error);
    setRestaurants([]);
  }), []);

  useEffect(() => {
    if (!uid) return undefined;
    fetchOwnRestaurant(uid).then(setOwnRestaurant).catch(() => setOwnRestaurant(null));
    return subscribeOrdersAsBuyer(uid, setOrders, (error) => console.error("Error cargando pedidos:", error));
  }, [uid]);

  const appBarRef = useRef(null);
  const [appBarHeight, setAppBarHeight] = useState(96);
  useLayoutEffect(() => {
    const node = appBarRef.current;
    if (!node) {
      setAppBarHeight(0);
      return undefined;
    }
    const updateHeight = () => setAppBarHeight(node.offsetHeight);
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(node);
    return () => observer.disconnect();
  }, [showTopBar, isSearchOpen]);

  const activeOrder = orders.find((o) => ACTIVE_STATUSES.includes(o.status));

  // Los locales se muestran solo de la ciudad donde está el usuario (como
  // autos, con useGeolocation + getCityFromCoordinates). La ciudad se
  // resuelve una sola vez: watchPosition dispara seguido y Nominatim limita.
  // Sin permiso de ubicación se muestran todos.
  const { position } = useGeolocation();
  const userPos = useMemo(() => (position ? { lat: position.latitude, lng: position.longitude } : null), [position]);
  const [userCity, setUserCity] = useState(null);
  const cityRequested = useRef(false);
  useEffect(() => {
    if (!position || cityRequested.current) return;
    cityRequested.current = true;
    getCityFromCoordinates(position.latitude, position.longitude).then(setUserCity);
  }, [position]);

  const inUserCity = (restaurant) => {
    if (!userCity) return true;
    if (restaurant.city) return normalize(restaurant.city.trim()) === normalize(userCity);
    return distanceKm(userPos, restaurant) <= 30;
  };

  const sortOptions = SORT_OPTIONS.filter((o) => o.id !== "near" || userPos);
  const chooseSort = (next) => {
    setSortAnchor(null);
    setSort(next);
  };

  const activeFilters = [sort, openOnly, freeDelivery, withDelivery, noMinimum, category].filter(Boolean).length;
  const clearFilters = () => {
    setSort("");
    setOpenOnly(false);
    setFreeDelivery(false);
    setWithDelivery(false);
    setNoMinimum(false);
    setCategory("");
  };

  const visible = useMemo(() => {
    if (!restaurants) return null;
    const term = normalize(search.trim());
    const byOpen = (a, b) => Number(isRestaurantOpen(b)) - Number(isRestaurantOpen(a));
    const byName = (a, b) => String(a.name).localeCompare(String(b.name));
    const sorters = {
      near: (a, b) => distanceKm(userPos, a) - distanceKm(userPos, b),
      fast: (a, b) => deliveryMinutes(a) - deliveryMinutes(b),
      fee: (a, b) => deliveryFeeOf(a) - deliveryFeeOf(b),
    };
    return restaurants
      .filter((r) => storeKind(r) === kind)
      .filter(inUserCity)
      .filter((r) => kind === "market" || !meal || !Array.isArray(r.mealTypes) || !r.mealTypes.length || r.mealTypes.includes(meal))
      .filter((r) => !category || r.category === category)
      .filter((r) => !openOnly || isRestaurantOpen(r))
      .filter((r) => !withDelivery || r.delivery !== false)
      .filter((r) => !freeDelivery || (r.delivery !== false && !(Number(r.deliveryFee) > 0)))
      .filter((r) => !noMinimum || !(Number(r.minOrder) > 0))
      .filter((r) => !term || normalize(`${r.name} ${r.description} ${categoryOf(r.category).label} ${(r.tags || []).join(" ")}`).includes(term))
      .sort((a, b) => {
        if (sort === "name") return byName(a, b);
        return byOpen(a, b) || (sorters[sort] ? sorters[sort](a, b) || 0 : 0) || byName(a, b);
      });
    // inUserCity depende solo de userCity/userPos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurants, search, meal, category, kind, userCity, openOnly, withDelivery, freeDelivery, noMinimum, sort, userPos]);

  const toggleChip = (active) => ({ color: active ? "primary" : "default", variant: active ? "filled" : "outlined" });

  const searchField = (
    <TextField
      fullWidth
      size="small"
      autoFocus={!isDesktop}
      placeholder={kind === "market" ? "Buscar supermercados y almacenes" : "Buscar locales o comidas"}
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      autoComplete="off"
      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 999 } }}
      InputProps={{
        startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
        endAdornment: search ? (
          <InputAdornment position="end">
            <IconButton size="small" onClick={() => setSearch("")} aria-label="Borrar búsqueda">
              <CloseIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />
  );

  const toggleChips = (
    <>
      <Chip label="Abierto ahora" onClick={() => setOpenOnly((v) => !v)} {...toggleChip(openOnly)} />
      <Chip label="Envío gratis" onClick={() => setFreeDelivery((v) => !v)} {...toggleChip(freeDelivery)} />
      <Chip label="Con delivery" onClick={() => setWithDelivery((v) => !v)} {...toggleChip(withDelivery)} />
      <Chip label="Sin mínimo" onClick={() => setNoMinimum((v) => !v)} {...toggleChip(noMinimum)} />
    </>
  );

  const mealLabel = MEAL_TYPES.find((m) => m.id === meal)?.label.toLowerCase();

  return (
    <Container maxWidth={false} disableGutters sx={pageContentSx}>
      {showTopBar && (
        <AppBar ref={appBarRef} position="fixed" elevation={0} sx={{ top: 0, width: "100%", bgcolor: barColors.background, zIndex: 1100 }}>
          {!sidebarActive && (
            <Toolbar sx={{ display: "flex", justifyContent: "center", padding: "8px 0" }}>
              <CardMedia
                component="img"
                image={themeMode === "dark" ? logo5 : brandColors.mode === "light" ? logo1 : logoSvg}
                title="Logo"
                style={{ width: "130px", height: "auto", aspectRatio: "847 / 231" }}
              />
            </Toolbar>
          )}
          <CustomToolbar
            isSearchOpen={isSearchOpen}
            onToggleSearch={() => setIsSearchOpen((open) => !open)}
            onOpenFilters={() => setFiltersOpen(true)}
            showSearchFilters={!isDesktop}
          />
          {!isDesktop && isSearchOpen && (
            <Box sx={{ bgcolor: barColors.background, px: 2, py: 1 }}>
              <Box sx={{ ...pageContentSx }}>{searchField}</Box>
            </Box>
          )}
        </AppBar>
      )}

      <Box sx={{ mt: `${appBarHeight}px`, mb: 12, pt: 2, maxWidth: 1100, mx: "auto" }}>
        <Box sx={{ display: "flex", gap: 1, mb: 2, p: 0.5, borderRadius: 999, bgcolor: "action.hover" }}>
          {STORE_KINDS.map((k) => (
            <Button
              key={k.id}
              onClick={() => setKind(k.id)}
              disableElevation
              variant={kind === k.id ? "contained" : "text"}
              color={kind === k.id ? "primary" : "inherit"}
              sx={{ flex: 1, borderRadius: 999, fontWeight: 700 }}
            >
              {k.emoji} {k.label}
            </Button>
          ))}
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 800 }}>
          {kind === "market" ? "Hacé las compras del súper sin salir de casa" : meal ? `¿Qué pedimos para ${meal === "cena" ? "la" : "el"} ${mealLabel}?` : "¿Qué pedimos hoy?"}
        </Typography>

        {userCity && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5, color: "text.secondary" }}>
            <LocationOnIcon sx={{ fontSize: 16 }} />
            <Typography variant="body2">{userCity}</Typography>
          </Box>
        )}

        {isDesktop && <Box sx={{ mt: 1.5 }}>{searchField}</Box>}

        <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
          <Button size="small" variant="outlined" startIcon={<ReceiptLongIcon />} component={Link} to="/pedidos/mis-pedidos" sx={{ borderRadius: 999 }}>
            <T>Mis pedidos</T>
          </Button>
          <Button size="small" variant="outlined" startIcon={<StorefrontIcon />} component={Link} to="/pedidos/mi-local" sx={{ borderRadius: 999 }}>
            {ownRestaurant ? <T>Mi local</T> : <T>Sumá tu local</T>}
          </Button>
        </Box>

        {activeOrder && (
          <Paper
            onClick={() => navigate(`/pedidos/orden/${activeOrder.id}`)}
            sx={{ mt: 2, p: 1.5, borderRadius: 3, display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer", bgcolor: "primary.main", color: "primary.contrastText" }}
          >
            <DeliveryDiningIcon sx={{ fontSize: 36 }} />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                {activeOrder.restaurantName}
              </Typography>
              <Typography variant="body2">{ORDER_STATUS[activeOrder.status]?.label}</Typography>
            </Box>
            <ChevronRightIcon />
          </Paper>
        )}

        {kind === "food" && (
        <ScrollRow sx={{ mt: 2 }}>
          <Chip label="Todo el día" onClick={() => setMeal("")} color={meal ? "default" : "primary"} variant={meal ? "outlined" : "filled"} />
          {MEAL_TYPES.map((m) => (
            <Chip key={m.id} label={m.label} onClick={() => setMeal(m.id)} color={meal === m.id ? "primary" : "default"} variant={meal === m.id ? "filled" : "outlined"} />
          ))}
        </ScrollRow>
        )}

        <ScrollRow key={kind} gap={1.5} sx={{ mt: 2, mb: 0.5 }}>
          {categories.map((c) => {
            const selected = category === c.id;
            return (
              <Box
                key={c.id}
                component="button"
                type="button"
                onClick={() => setCategory(selected ? "" : c.id)}
                sx={{ flex: "0 0 auto", width: 76, border: 0, bgcolor: "transparent", color: "inherit", cursor: "pointer", p: 0, font: "inherit" }}
              >
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    mx: "auto",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 30,
                    bgcolor: selected ? "action.selected" : "action.hover",
                    outline: selected ? "2px solid" : "none", outlineColor: "primary.main",
                    outlineOffset: 2,
                  }}
                >
                  {c.emoji}
                </Box>
                <Typography variant="caption" sx={{ display: "block", mt: 0.5, lineHeight: 1.2, fontWeight: selected ? 700 : 400 }}>
                  {c.label}
                </Typography>
              </Box>
            );
          })}
        </ScrollRow>

        {isDesktop && (
        <ScrollRow sx={{ mt: 1.5 }}>
          <Chip
            icon={<FilterListIcon />}
            label={SORT_OPTIONS.find((o) => o.id === sort).label}
            deleteIcon={<ExpandMoreIcon />}
            onDelete={(e) => setSortAnchor(e.currentTarget.closest(".MuiChip-root"))}
            onClick={(e) => setSortAnchor(e.currentTarget)}
            {...toggleChip(!!sort)}
          />
          {toggleChips}
          {activeFilters > 0 && (
            <Chip label="Limpiar" icon={<CloseIcon />} onClick={clearFilters} variant="outlined" />
          )}
        </ScrollRow>
        )}

        {!isDesktop && (
          <Drawer
            anchor="bottom"
            open={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: "85vh" } }}
          >
            <Box sx={{ pt: 1.5, pb: 4, px: 2, overflowY: "auto" }}>
              <SheetDragHandle onClose={() => setFiltersOpen(false)} />
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}><T>Filtros</T></Typography>
                <IconButton onClick={() => setFiltersOpen(false)} size="small" aria-label="Cerrar">
                  <CloseIcon />
                </IconButton>
              </Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}><T>Ordenar por</T></Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2.5 }}>
                {sortOptions.map((o) => (
                  <Chip key={o.id} label={o.label} onClick={() => setSort(o.id)} {...toggleChip(sort === o.id)} />
                ))}
              </Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}><T>Mostrar</T></Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>{toggleChips}</Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button fullWidth variant="outlined" onClick={clearFilters} disabled={!activeFilters}>
                  <T>Limpiar</T>
                </Button>
                <Button fullWidth variant="contained" onClick={() => setFiltersOpen(false)}>
                  {visible ? (visible.length === 1 ? "Ver 1 local" : `Ver ${visible.length} locales`) : "Ver locales"}
                </Button>
              </Box>
            </Box>
          </Drawer>
        )}

        <Menu anchorEl={sortAnchor} open={!!sortAnchor} onClose={() => setSortAnchor(null)}>
          {sortOptions.map((o) => (
            <MenuItem key={o.id} selected={sort === o.id} onClick={() => chooseSort(o.id)}>
              {o.label}
            </MenuItem>
          ))}
        </Menu>

        {visible && (search.trim() || activeFilters > 0) && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              {visible.length === 1 ? "1 local" : `${visible.length} locales`}
            </Typography>
            {!isDesktop && (
              <Button size="small" onClick={() => { clearFilters(); setSearch(""); }}>
                <T>Limpiar</T>
              </Button>
            )}
          </Box>
        )}

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr" }, gap: 2, mt: 1 }}>
          {visible === null
            ? Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={230} sx={{ borderRadius: 3 }} />
              ))
            : visible.map((r) => <RestaurantCard key={r.id} restaurant={r} />)}
        </Box>

        {visible && !visible.length && (
          <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
            <Typography sx={{ fontSize: 48 }}>{kind === "market" ? "🛒" : "🍽️"}</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {restaurants?.some((r) => storeKind(r) === kind && inUserCity(r))
                ? <T>No hay locales con esos filtros</T>
                : userCity
                ? `${kind === "market" ? "Todavía no hay supermercados" : "Todavía no hay locales"} en ${userCity}`
                : kind === "market" ? <T>Todavía no hay supermercados en Notiline</T> : <T>Todavía no hay locales en Notiline</T>}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {kind === "market"
                ? <T>¿Tenés un súper, almacén o verdulería? Sumalo y recibí pedidos: está incluido en todos los planes.</T>
                : <T>¿Tenés un restaurante, rotisería o cafetería? Sumalo y recibí pedidos: está incluido en todos los planes.</T>}
            </Typography>
            {activeFilters > 0 || search.trim() ? (
              <Button variant="outlined" onClick={() => { clearFilters(); setSearch(""); }}>
                <T>Limpiar filtros</T>
              </Button>
            ) : (
              <Button variant="contained" component={Link} to="/pedidos/mi-local">
                <T>Sumá tu local</T>
              </Button>
            )}
          </Box>
        )}
      </Box>

      <BottomToolbar />
    </Container>
  );
};

export default FoodHome;
